// vim: set ts=2 sts=2 sw=2 et:
//
// HTTP client for the WRPF meet-snapshot sync (multi-PC). Each PC is identified by a deviceId (not
// the session — the shared link reuses PC 1's token). Auth is the same Bearer JWT used for bundle.

import type { OpenlifterBundleFile } from "./types";

export type OwnedPlatform = { day: number; platform: number };

export type SyncDeviceSummary = {
  deviceId: string;
  label: string | null;
  ownedPlatforms: OwnedPlatform[];
  entryCount: number;
  updatedAt: string;
};

const SESSION_EXPIRED =
  "Tu sesión con WRPF expiró. Pedile al admin que reabra el torneo. Podés seguir operando y guardar el .openlifter localmente.";

/** Error HTTP del sync, con el status para distinguir "no hay copia" (404) de una falla real. */
export class SyncHttpError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

// Netlify corta los pedidos de más de 6 MB. Lo que se sube va sin récords (buildWebUploadBundle) y pesa
// menos de 1 MB para un torneo como el Ibero; si igual pasara, que se entienda qué hacer.
const TOO_LARGE = "El torneo es demasiado grande para subirlo a la web. Guardalo con «Guardar como» en Inicio.";

async function syncFetch(url: string, token: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    if (response.status === 401) throw new SyncHttpError(SESSION_EXPIRED, 401);
    if (response.status === 413) throw new SyncHttpError(TOO_LARGE, 413);
    const text = await response.text();
    throw new SyncHttpError(
      `Sync ${init?.method ?? "GET"} falló (${response.status}): ${text.slice(0, 240)}`,
      response.status,
    );
  }
  return response;
}

const withQuery = (syncUrl: string, key: string, value: string): string => {
  const separator = syncUrl.includes("?") ? "&" : "?";
  return `${syncUrl}${separator}${key}=${encodeURIComponent(value)}`;
};

/**
 * Sube el snapshot de ESTA PC (export bundle + identidad + tarima declarada). Sin `label`, la web usa
 * la etiqueta de la sesión ("Tarima N", el número que se eligió al abrir desde la web).
 */
export async function pushMeetSnapshot(
  syncUrl: string,
  token: string,
  params: {
    deviceId: string;
    label?: string;
    ownedPlatforms: OwnedPlatform[];
    bundle: unknown;
  },
): Promise<{ ok: boolean; updatedAt: string; entryCount: number }> {
  const response = await syncFetch(syncUrl, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      deviceId: params.deviceId,
      label: params.label,
      ownedPlatforms: params.ownedPlatforms,
      bundle: params.bundle,
    }),
  });
  return (await response.json()) as { ok: boolean; updatedAt: string; entryCount: number };
}

/**
 * "Enviar resultados a la web": sube el snapshot de ESTA PC y la web fusiona todas las tarimas en un
 * único ranking. Devuelve el conteo fusionado, cuántos quedaron sin vincular y cuántas PCs reportaron.
 */
export async function publishAndFuse(
  publishUrl: string,
  token: string,
  params: {
    deviceId: string;
    label: string;
    ownedPlatforms: OwnedPlatform[];
    bundle: unknown;
  },
): Promise<{ ok: boolean; count: number; pendingMatches: number; devices: number }> {
  const response = await syncFetch(publishUrl, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      deviceId: params.deviceId,
      label: params.label,
      ownedPlatforms: params.ownedPlatforms,
      bundle: params.bundle,
    }),
  });
  return (await response.json()) as {
    ok: boolean;
    count: number;
    pendingMatches: number;
    devices: number;
  };
}

/** Lista la metadata (sin payload) de las OTRAS PCs del torneo. */
export async function listOtherDevices(syncUrl: string, token: string, deviceId: string): Promise<SyncDeviceSummary[]> {
  const response = await syncFetch(withQuery(syncUrl, "deviceId", deviceId), token);
  const data = (await response.json()) as { devices?: SyncDeviceSummary[] };
  return data.devices ?? [];
}

/** Baja el export bundle completo de una PC (por deviceId) para fusionar. */
export async function fetchDeviceSnapshot(
  syncUrl: string,
  token: string,
  targetDeviceId: string,
): Promise<OpenlifterBundleFile> {
  const response = await syncFetch(withQuery(syncUrl, "target", targetDeviceId), token);
  const data = (await response.json()) as { payload?: OpenlifterBundleFile };
  if (!data.payload) {
    throw new Error("La PC remota todavía no subió datos.");
  }
  return data.payload;
}

/** Como fetchDeviceSnapshot, pero null si esa PC todavía no subió nada. */
export async function fetchDeviceSnapshotOrNull(
  syncUrl: string,
  token: string,
  targetDeviceId: string,
): Promise<OpenlifterBundleFile | null> {
  try {
    return await fetchDeviceSnapshot(syncUrl, token, targetDeviceId);
  } catch (err) {
    if ((err as Partial<SyncHttpError>).status === 404) return null;
    throw err;
  }
}
